<template>
  <div class="company-profile-page">
    <!-- 内页头 -->
    <inner-hero
      title="企业简介"
      parent-label="企业概况"
      background-image="/images/company/profile-hero.webp"
      description="说明海南兴科是谁、做什么，以及如何连接科研资源、产品研发和产业服务。"
    />

    <!-- 企业概况 -->
    <section class="about-section profile-section" data-od-id="profile-overview">
      <div class="profile-content page-container">
        <div class="profile-copy">
          <span class="section-kicker">ABOUT XINGKE</span>
          <h2>海南兴科</h2>
          <p>
            海南兴科立足海南热带作物资源，围绕咖啡、胡椒、可可等特色作物，构建科研平台、中试验证、产品研发与产业服务相互连接的能力体系。
          </p>
          <p>
            面向绿色加工、风味调控、功能产品开发及智能装备等方向，团队持续推进技术研究与工艺验证，让科研成果从实验室走向可复制、可应用的产品与解决方案。
          </p>
          <p>
            依托兴隆热带植物园及相关产业场景，海南兴科将专业能力延伸至企业合作和公众科普，为热带特色作物的价值提升提供长期支持。
          </p>
          <dl class="profile-metrics" aria-label="企业能力数据">
            <div>
              <dt><strong>15,000+</strong></dt>
              <dd>平方米中试基地</dd>
            </div>
            <div>
              <dt><strong>8</strong></dt>
              <dd>条中试生产线</dd>
            </div>
            <div>
              <dt><strong>700+</strong></dt>
              <dd>台（套）研发及生产设备</dd>
            </div>
            <div>
              <dt><strong>50+</strong></dt>
              <dd>项授权发明专利</dd>
            </div>
          </dl>
        </div>
        <button
          class="profile-cover"
          type="button"
          aria-label="播放海南兴科企业简介视频"
          @click="openProfileVideo"
        >
          <img
            src="/images/company/profile-video-cover.webp"
            alt="海南兴科中试基地与研发场景"
            width="1440"
            height="878"
            loading="lazy"
            decoding="async"
            @error="onImgError"
          />
          <span class="profile-play" aria-hidden="true">
            <i class="i-lucide-play"></i>
          </span>
          <span class="profile-media-state" aria-live="polite">
            以中试验证连接实验室研究与产业应用
          </span>
        </button>
      </div>
    </section>

    <profile-video-dialog
      :open="isProfileVideoOpen"
      :video-url="profileVideoUrl"
      poster="/images/company/profile-video-cover.webp"
      title="海南兴科"
      description="以中试验证连接实验室研究与产业应用"
      @update:open="setProfileVideoOpen"
    />

    <!-- 企业文化 -->
    <section class="about-section soft culture-section" data-od-id="corporate-culture">
      <div class="page-container">
        <span class="section-kicker">CORPORATE CULTURE</span>
        <h2>企业文化</h2>
        <ui-expandable-gallery
          :items="cultures"
          class="culture-gallery"
          role="list"
          aria-label="企业文化"
        >
          <template #default="{ item, active }">
            <div class="culture-card" :class="{ active }">
              <span class="culture-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" :data-icon="item.icon">
                  <path
                    fill="none"
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    :d="item.path"
                  />
                </svg>
              </span>
              <span class="culture-label">{{ item.label }}</span>
              <span class="culture-detail">{{ item.detail }}</span>
            </div>
          </template>
        </ui-expandable-gallery>
      </div>
    </section>

    <!-- 发展历程 -->
    <section class="about-section milestones-section" data-od-id="development-milestones">
      <div class="page-container">
        <span class="section-kicker">DEVELOPMENT</span>
        <h2>发展历程</h2>
        <p class="milestones-intro">
          从热带作物科研起步，逐步构建研发平台、中试体系与多元产业化能力。
        </p>
        <div
          ref="milestoneTrack"
          class="milestone-track"
          role="tablist"
          aria-label="发展历程"
          @keydown="onMilestoneKeydown"
        >
          <button
            v-for="m in visibleMilestones"
            :key="m.year"
            class="milestone-card"
            :class="{ active: m.index === activeMilestone }"
            :data-milestone-index="m.index"
            type="button"
            role="tab"
            :aria-selected="m.index === activeMilestone"
            :tabindex="m.index === activeMilestone ? 0 : -1"
            @click="selectMilestone(m.index)"
          >
            <span class="milestone-year">{{ m.year }}</span>
            <strong class="milestone-title">{{ m.title }}</strong>
            <em class="milestone-summary" :aria-hidden="m.index !== activeMilestone">
              {{ m.summary }}
            </em>
          </button>
        </div>
        <div class="milestone-controls">
          <button
            type="button"
            aria-label="查看上一组发展历程"
            :disabled="activeMilestone === 0"
            @click="moveMilestone(-1)"
          >
            <span aria-hidden="true">←</span>
          </button>
          <span class="milestone-current" aria-live="polite">
            {{ activeMilestoneLabel }}
          </span>
          <button
            type="button"
            aria-label="查看下一组发展历程"
            :disabled="activeMilestone === milestones.length - 1"
            @click="moveMilestone(1)"
          >
            <span aria-hidden="true">→</span>
          </button>
        </div>
      </div>
    </section>

    <!-- 荣誉资质 -->
    <section class="about-section soft" data-od-id="brand-honors">
      <div class="honors-layout page-container">
        <figure class="honor-stage">
          <img
            src="/images/company/honors.webp"
            alt="海南兴科荣誉资质陈列原型视觉"
            width="1280"
            height="960"
            loading="lazy"
            decoding="async"
            @error="onImgError"
          />
          <figcaption>海南兴科荣誉资质陈列展示</figcaption>
        </figure>
        <div>
          <span class="section-kicker">BRAND HONORS</span>
          <h2>荣誉资质</h2>
          <honor-infinite-list :items="honors" aria-label="荣誉资质列表" />
        </div>
      </div>
    </section>

    <!-- 研发团队 -->
    <section class="about-section" data-od-id="research-team">
      <div class="team-layout page-container">
        <div class="team-copy">
          <span class="section-kicker">EXCELLENT TEAM</span>
          <h2>研发团队</h2>
          <p class="team-lead">让科研价值抵达产业现场</p>
          <p>
            公司现有员工200+，研发团队中高级职称7人、硕博士13人。团队围绕热带特色作物研究、中试验证、产品开发和产业服务持续积累专业能力。
          </p>
          <blockquote>
            以科研创新为起点，以中试验证为桥梁，让专业能力转化为可复制、可应用的产业成果。
          </blockquote>
        </div>
        <div class="team-showcase">
          <figure class="team-visual">
            <img
              src="/images/company/research-team.webp"
              alt="研发、中试与产业服务协同团队场景"
              width="1600"
              height="800"
              loading="lazy"
              decoding="async"
              @error="onImgError"
            />
            <figcaption>研发团队场景展示，覆盖科研、中试与成果转化协同角色。</figcaption>
          </figure>
        </div>
        <div class="team-grid" aria-label="研发团队六项协同能力">
          <article v-for="role in teamRoles" :key="role.label">
            <span class="team-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path
                  fill="none"
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  :d="role.path"
                />
              </svg>
            </span>
            <h3>{{ role.label }}</h3>
          </article>
        </div>
      </div>
    </section>

    <!-- 员工风采 -->
    <section class="about-section soft" data-od-id="staff-life">
      <div class="page-container">
        <span class="section-kicker">STAFF LIFE</span>
        <h2>员工风采</h2>
        <div class="staff-mosaic">
          <figure v-for="staff in staffItems" :key="staff.title" class="staff-item">
            <img
              :src="staff.image"
              :alt="staff.alt"
              width="1200"
              :height="staff.height"
              loading="lazy"
              decoding="async"
              @error="onImgError"
            />
            <figcaption class="staff-overlay">
              <strong>{{ staff.title }}</strong>
              <span>{{ staff.desc }}</span>
            </figcaption>
          </figure>
        </div>
      </div>
    </section>

    <!-- 联系行动区 -->
    <contact-action />
  </div>
</template>
<script setup lang="ts">
  import { useMediaQuery } from '@vueuse/core'

  import {
    createBreadcrumbJsonLd,
    createSiteUrl,
    getPageSeo,
    getSiteUrl,
    siteSeo,
  } from '~/utils/seo'

  /**
   * 企业简介页静态数据
   * 文案取自高保真原型，正式上线前须以客户确认资料为准
   */
  defineOptions({
    name: 'Company',
  })

  const seo = getPageSeo('company-profile')
  const siteUrl = getSiteUrl()

  useSeoMeta({
    title: seo.title,
    description: seo.description,
    ogTitle: seo.title,
    ogDescription: seo.description,
    ogType: 'article',
    ogImage: siteSeo.ogImage,
    twitterTitle: seo.title,
    twitterDescription: seo.description,
    twitterImage: siteSeo.ogImage,
  })

  useHead({
    meta: [{ name: 'keywords', content: seo.keywords?.join(', ') }],
    link: [{ rel: 'canonical', href: createSiteUrl('/company/profile', siteUrl) }],
  })
  useJsonLd(
    createBreadcrumbJsonLd(
      [
        { name: '首页', path: '/' },
        { name: '企业简介', path: '/company/profile' },
      ],
      siteUrl
    )
  )

  /** 企业简介宣传视频地址（临时签名地址），后续替换为正式媒体配置。 */
  const profileVideoUrl =
    'https://cloud.video.taobao.com/play/u/28277659/p/1/d/hd_265/e/6/t/1/2961975526795242.mp4?auth_key=YXBwX2tleT04MDAwMDAwMTImYXV0aF9pbmZvPXsidGltZXN0YW1wRW5jcnlwdGVkIjoiYmFlNWExMGZmZDU3NTcwNzE3MDAwZGJhYTIxNDM1M2YifSZkdXJhdGlvbj0mdGltZXN0YW1wPTE3ODg3NTAxNzk='
  const isProfileVideoOpen = shallowRef(false)

  /** 打开企业简介视频弹窗。 */
  function openProfileVideo() {
    isProfileVideoOpen.value = true
  }

  /** 同步视频弹窗的受控开合状态。 */
  function setProfileVideoOpen(open: boolean) {
    isProfileVideoOpen.value = open
  }

  /** 图片加载失败时添加 is-error 类，触发 ::before 占位 */
  function onImgError(e: Event) {
    const img = e.target as HTMLImageElement
    img.classList.add('is-error')
  }

  const cultures = [
    {
      label: '扎根海南',
      detail: '从真实产业问题中确定研究方向。',
      icon: 'lucide:sprout',
      path: 'M14 9.536V7a4 4 0 0 1 4-4h1.5a.5.5 0 0 1 .5.5V5a4 4 0 0 1-4 4a4 4 0 0 0-4 4c0 2 1 3 1 5a5 5 0 0 1-1 3M4 9a5 5 0 0 1 8 4a5 5 0 0 1-8-4m1 12h14',
    },
    {
      label: '尊重科学',
      detail: '以实验结果和中试数据为依据。',
      icon: 'lucide:flask-conical',
      path: 'M14 2v6a2 2 0 0 0 .245.96l5.51 10.08A2 2 0 0 1 18 22H6a2 2 0 0 1-1.755-2.96l5.51-10.08A2 2 0 0 0 10 8V2M6.453 15h11.094M8.5 2h7',
    },
    {
      label: '面向产业',
      detail: '推动成果落地、复制和持续优化。',
      icon: 'lucide:factory',
      path: 'M12 16h.01M16 16h.01M3 19a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8.5a.5.5 0 0 0-.769-.422l-4.462 2.844A.5.5 0 0 1 15 10.5v-2a.5.5 0 0 0-.769-.422L9.77 10.922A.5.5 0 0 1 9 10.5V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2zm5-3h.01',
    },
    {
      label: '长期主义',
      detail: '持续积累热带作物研究与服务能力。',
      icon: 'lucide:infinity',
      path: 'M6 16c5 0 7-8 12-8a4 4 0 0 1 0 8c-5 0-7-8-12-8a4 4 0 1 0 0 8',
    },
  ]

  const milestones = [
    {
      year: '1985 年',
      title: '科研加工起步',
      summary: '前身建厂，启动咖啡、可可等热带作物加工研究。',
    },
    {
      year: '1997 年',
      title: '开启品牌运营',
      summary: '正式注册“兴科”商标，开启品牌化运营。',
    },
    {
      year: '2011 年',
      title: '确立产业化主体',
      summary: '12 月 22 日改制成立海南兴科，确立科技产业化主体地位。',
    },
    {
      year: '2015 年',
      title: '拓展高值化产品',
      summary: '推出冻干果干、巧克力、香水等系列高值化产品。',
    },
    {
      year: '2021 年',
      title: '打通国际供应链',
      summary: '获评省级先进集体，实现海南原产地可可豆出口比利时。',
    },
    {
      year: '2022 年',
      title: '完成吸收合并',
      summary: '与兴园公司吸收合并组建新主体，完成架构优化。',
    },
    {
      year: '2023 年',
      title: '销售破亿与国际化',
      summary: '年销售额首破亿元，获海南老字号认定并进军国际市场。',
    },
    {
      year: '2025 年',
      title: '获得 AEO 高级认证',
      summary: '7 月成为海南首家可可全产业链获此认证的龙头企业。',
    },
    {
      year: '2026 年',
      title: '迈入国家级科研平台',
      summary: '牵头获批建设农业农村部重点实验室。',
    },
  ]

  const milestonePageSize = 4
  const isMobile = useMediaQuery('(max-width: 767.98px)')
  const activeMilestone = shallowRef(0)
  const milestoneTrack = useTemplateRef<HTMLElement>('milestoneTrack')
  const activeMilestonePage = computed(() => Math.floor(activeMilestone.value / milestonePageSize))
  const visibleMilestones = computed(() => {
    // 移动端展示全部，PC 端按 pageSize 分页
    if (isMobile.value) {
      return milestones.map((milestone, index) => ({ ...milestone, index }))
    }
    const start = activeMilestonePage.value * milestonePageSize
    return milestones
      .slice(start, start + milestonePageSize)
      .map((milestone, offset) => ({ ...milestone, index: start + offset }))
  })
  const activeMilestoneLabel = computed(() => milestones[activeMilestone.value]?.year ?? '')

  /** 切换发展阶段，并在横向轨道溢出时将选中项滚动至视野。 */
  async function selectMilestone(index: number, shouldFocus = false) {
    const nextIndex = Math.min(Math.max(index, 0), milestones.length - 1)
    activeMilestone.value = nextIndex
    await nextTick()

    const track = milestoneTrack.value
    const card = track?.querySelector<HTMLButtonElement>(`[data-milestone-index="${nextIndex}"]`)
    if (!card) return
    if (shouldFocus) card.focus()
    if (!track) return

    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 'auto'
      : 'smooth'
    track.scrollTo({
      left: Math.max(0, card.offsetLeft - (track.clientWidth - card.offsetWidth) / 2),
      behavior,
    })
  }

  /** 按时间顺序查看上一项或下一项，跨过四项边界时切换分组。 */
  function moveMilestone(step: -1 | 1) {
    selectMilestone(activeMilestone.value + step)
  }

  /** 支持当前分组内的左右方向键及 Home、End 键导航。 */
  function onMilestoneKeydown(event: KeyboardEvent) {
    const keyOffsets: Partial<Record<KeyboardEvent['key'], number>> = {
      ArrowLeft: -1,
      ArrowRight: 1,
    }
    const isBoundaryKey = event.key === 'Home' || event.key === 'End'
    const offset = keyOffsets[event.key]
    if (offset === undefined && !isBoundaryKey) return

    event.preventDefault()
    // 移动端无分页边界，直接在整个列表内导航
    if (isMobile.value) {
      let nextIndex = Math.min(
        milestones.length - 1,
        Math.max(0, activeMilestone.value + (offset ?? 0))
      )
      if (event.key === 'Home') nextIndex = 0
      if (event.key === 'End') nextIndex = milestones.length - 1
      selectMilestone(nextIndex, true)
      return
    }
    const pageStart = activeMilestonePage.value * milestonePageSize
    const pageEnd = Math.min(pageStart + milestonePageSize - 1, milestones.length - 1)
    let nextIndex = Math.min(pageEnd, Math.max(pageStart, activeMilestone.value + (offset ?? 0)))
    if (event.key === 'Home') nextIndex = pageStart
    if (event.key === 'End') nextIndex = pageEnd
    selectMilestone(nextIndex, true)
  }

  /** 移动端滚动停止后同步激活态 */
  let milestoneScrollTimer: ReturnType<typeof setTimeout> | undefined

  function onMilestoneScroll() {
    if (!isMobile.value) return
    const track = milestoneTrack.value
    if (!track) return

    if (milestoneScrollTimer) clearTimeout(milestoneScrollTimer)
    milestoneScrollTimer = setTimeout(() => {
      const centerX = track.scrollLeft + track.clientWidth / 2
      const cards = track.querySelectorAll<HTMLButtonElement>('[data-milestone-index]')
      let minDist = Infinity
      let result = activeMilestone.value
      cards.forEach((card) => {
        const idx = Number(card.dataset.milestoneIndex)
        const center = card.offsetLeft + card.offsetWidth / 2
        const dist = Math.abs(center - centerX)
        if (dist < minDist) {
          minDist = dist
          result = idx
        }
      })
      if (result !== activeMilestone.value) {
        activeMilestone.value = result
      }
    }, 60)
  }

  onMounted(() => {
    const track = milestoneTrack.value
    if (track) {
      track.addEventListener('scroll', onMilestoneScroll, { passive: true })
    }
  })

  onBeforeUnmount(() => {
    const track = milestoneTrack.value
    if (track) {
      track.removeEventListener('scroll', onMilestoneScroll)
    }
    if (milestoneScrollTimer) clearTimeout(milestoneScrollTimer)
  })

  const honors = [
    '国家高新技术企业',
    'ISO9001 质量管理体系认证',
    '知识产权管理体系认证',
    '出口食品生产企业备案',
    '省部级以上科技奖励 43 项',
    '“兴科”商标连续 3 次获海南省著名商标',
    '海南省名牌产品',
    '中国国际高新技术成果交易会优秀产品奖',
    '中国特色旅游商品大赛银奖',
    '海南省消费者协会可信商品',
  ]

  const teamRoles = [
    {
      label: '科研创新',
      path: 'M20.2 20.2c2.04-2.03.02-7.36-4.5-11.9c-4.54-4.52-9.87-6.54-11.9-4.5c-2.04 2.03-.02 7.36 4.5 11.9c4.54 4.52 9.87 6.54 11.9 4.5M15.7 15.7c4.52-4.54 6.54-9.87 4.5-11.9c-2.03-2.04-7.36-.02-11.9 4.5c-4.52 4.54-6.54 9.87-4.5 11.9c2.03 2.04 7.36.02 11.9-4.5',
    },
    {
      label: '工艺验证',
      path: 'M14 2v6a2 2 0 0 0 .245.96l5.51 10.08A2 2 0 0 1 18 22H6a2 2 0 0 1-1.755-2.96l5.51-10.08A2 2 0 0 0 10 8V2M6.453 15h11.094M8.5 2h7',
    },
    {
      label: '产品开发',
      path: 'M12 22V12m4 5h6m-3-3v6m2-9.465V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.729l7 4a2 2 0 0 0 2 .001l1.675-.955M3.29 7L12 12l8.71-5M7.5 4.27l8.997 5.148',
    },
    {
      label: '质量管理',
      path: 'M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z',
    },
    {
      label: '技术服务',
      path: 'm11 17l2 2a1 1 0 1 0 3-3m0 0l2.5 2.5a1 1 0 1 0 3-3l-3.88-3.88a3 3 0 0 0-4.24 0l-.88.88a1 1 0 1 1-3-3l2.81-2.81a5.79 5.79 0 0 1 7.06-.87l.47.28a2 2 0 0 0 1.42.25L21 4M21 3l1 11h-2M3 3L2 14l6.5 6.5a1 1 0 1 0 3-3M3 4h8',
    },
    {
      label: '成果转化',
      path: 'M3 3h8v8H3zM13 13h8v8h-8zM7 11v2a4 4 0 0 0 4 4h2M11 7h2a4 4 0 0 1 4 4v2',
    },
  ]

  const staffItems = [
    {
      title: '科研与产业现场',
      desc: '围绕热带作物加工与产品稳定性开展实验工作',
      image: '/images/company/staff-research.webp',
      alt: '海南兴科科研实验现场',
      height: 904,
    },
    {
      title: '产业交流与体验',
      desc: '连接产品展示、消费体验与产业合作场景',
      image: '/images/company/staff-visitor-cafe.webp',
      alt: '海南兴科产业交流场景',
      height: 675,
    },
    {
      title: '科普研学活动',
      desc: '面向学校、亲子与交流团体开展科普',
      image: '/images/company/staff-botanical.webp',
      alt: '热带植物科普研学场景',
      height: 675,
    },
    {
      title: '热带作物实践',
      desc: '感知从资源到产品的过程',
      image: '/images/company/staff-cocoa.webp',
      alt: '热带作物实践场景',
      height: 741,
    },
    {
      title: '基地协同活动',
      desc: '研发、中试、生产与服务团队协同推进项目',
      image: '/images/company/campus.webp',
      alt: '海南兴科研发与中试基地场景',
      height: 1067,
    },
  ]
</script>

<style scoped lang="scss">
  @keyframes milestone-summary-in {
    from {
      opacity: 0;
      transform: translateY(8px);
    }

    to {
      opacity: 1;
      transform: none;
    }
  }

  // ── 响应式 ──
  @include lt-sm {
    .company-profile-page {
      .profile-content,
      .honors-layout,
      .team-layout {
        grid-template-columns: 1fr;
      }

      .team-copy,
      .team-showcase,
      .team-grid {
        grid-column: 1;
      }

      .team-grid {
        grid-template-columns: repeat(3, minmax(0, 1fr));

        h3 {
          font-size: clamp(13px, 3.4vw, 15px);
        }
      }

      .profile-copy {
        max-width: 780px;
      }

      .profile-cover,
      .honor-stage,
      .team-visual {
        width: min(100%, 760px);
      }

      .milestone-track {
        grid-template-columns: repeat(9, calc(100vw - 48px));
        grid-auto-flow: column;
        width: 100vw;
        padding-inline: 24px;
        margin-inline: calc((100% - 100vw) / 2);
        overflow-x: auto;
        scroll-snap-type: x mandatory;
        scrollbar-width: none;

        &::-webkit-scrollbar {
          display: none;
        }
      }

      .milestone-card {
        scroll-snap-align: center;
        scroll-snap-stop: always;
        transition:
          background 320ms var(--ease),
          border-color 320ms var(--ease);

        .milestone-year {
          font-size: clamp(16px, 4.4vw, 20px);
        }

        .milestone-title {
          font-size: clamp(16px, 4.4vw, 20px);
        }

        .milestone-summary {
          font-size: clamp(12px, 3.2vw, 13px);
        }
      }

      .profile-metrics {
        grid-template-columns: 1fr 1fr;

        strong {
          font-size: clamp(22px, 5.6vw, 26px);
        }

        dd {
          font-size: clamp(11px, 2.8vw, 12px);
        }
      }

      /* 企业文化 gallery：移动端轮播模式 */
      .culture-gallery {
        --eg-active: 0;
        --eg-inactive: 0;
        --eg-item-shrink: 0;
        --eg-active-basis: 0%;
        --eg-inactive-basis: 0%;

        gap: 12px;

        /* 撑满屏幕宽度，溢出 page-container 的 24px padding */
        width: 100vw;

        /* 左右 padding 提供首尾卡片居中的滚动余量 */
        padding-inline: 24px;
        padding-bottom: 8px;
        margin-inline: calc((100% - 100vw) / 2);
        overflow-x: auto;
        scroll-snap-type: x mandatory;
        scrollbar-width: none;

        &::-webkit-scrollbar {
          display: none;
        }

        :deep(> div) {
          flex: 0 0 calc(100vw - 48px);
          width: calc(100vw - 48px);
          min-width: calc(100vw - 48px);
          scroll-snap-align: center;
          scroll-snap-stop: always;
        }
      }

      .culture-card {
        flex: 0 0 calc(100vw - 48px);
        width: calc(100vw - 48px);
        min-width: calc(100vw - 48px);
        height: 200px;
        transition:
          background 320ms var(--ease),
          color 320ms var(--ease),
          border-color 320ms var(--ease),
          box-shadow 320ms var(--ease);

        .culture-label {
          font-size: clamp(14px, 3.8vw, 17px);
        }

        .culture-detail {
          font-size: clamp(11px, 2.8vw, 12px);
          transition: opacity 320ms var(--ease) 60ms;
        }

        &.active {
          gap: 8px 18px;
          padding-inline: 28px;
          transition:
            background 320ms var(--ease),
            color 320ms var(--ease),
            border-color 320ms var(--ease),
            box-shadow 320ms var(--ease),
            gap 320ms var(--ease),
            padding 320ms var(--ease);

          .culture-icon {
            width: 62px;
            height: 62px;
            transition:
              width 320ms var(--ease),
              height 320ms var(--ease),
              border-color 320ms var(--ease),
              color 320ms var(--ease);
          }
        }
      }

      .culture-icon svg {
        width: 40px;
        height: 40px;
        transition:
          width 320ms var(--ease),
          height 320ms var(--ease);
      }

      .staff-mosaic {
        grid-template-rows: repeat(3, 220px);
        grid-template-columns: 1fr 1fr;

        .staff-item {
          &:first-child,
          &:last-child {
            grid-row: auto;
            grid-column: auto;
          }
        }
      }
    }
  }

  @include lt-xs {
    .company-profile-page {
      .profile-metrics {
        grid-template-columns: 1fr;
      }

      .team-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));

        article {
          min-height: 84px;
          padding: 14px;
        }
      }

      .staff-mosaic {
        grid-template-rows: none;
        grid-template-columns: 1fr;
      }

      .staff-item {
        min-height: 270px;
      }
    }
  }

  // ── 企业概况 profile-section ──
  .profile-section {
    background:
      radial-gradient(circle at 92% 12%, rgb(200 32 42 / 7%), transparent 34%),
      linear-gradient(135deg, var(--surface) 0 48%, #faf7f6 100%);

    &::after {
      position: absolute;
      right: -2vw;
      bottom: -7vw;
      font: 700 clamp(140px, 18vw, 300px) / 1 var(--num);
      color: rgb(200 32 42 / 2.5%);
      letter-spacing: -0.06em;
      pointer-events: none;
      content: 'XINGKE';
    }
  }

  .profile-content,
  .honors-layout,
  .team-layout {
    position: relative;
    z-index: 1;
    display: grid;
    grid-template-columns: 0.92fr 1.08fr;
    gap: clamp(48px, 6vw, 84px);
    align-items: center;
  }

  .profile-copy {
    > p {
      margin: 18px 0 0;
      line-height: 1.9;
      color: var(--muted);

      &:first-of-type {
        font-size: 17px;
        color: var(--fg);
      }
    }
  }

  .profile-metrics {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    margin: 30px 0 0;
    border-top: 1px solid var(--border);
    border-left: 1px solid var(--border);

    div {
      padding: 16px 18px;
      background: rgb(255 255 255 / 68%);
      border-right: 1px solid var(--border);
      border-bottom: 1px solid var(--border);
    }

    dt,
    dd {
      margin: 0;
    }

    strong {
      font: 650 clamp(26px, 2.4vw, 36px) / 1 var(--num);
      color: var(--accent);
    }

    dd {
      margin-top: 6px;
      font-size: 12px;
      color: var(--muted);
    }
  }

  .profile-cover {
    position: relative;
    width: 100%;
    padding: 0;
    overflow: hidden;
    color: var(--white);
    cursor: pointer;
    background: var(--fg);
    border: 0;
    border-radius: var(--radius-md);
    box-shadow: 0 26px 72px rgb(58 26 27 / 18%);

    &:focus-visible {
      outline: 3px solid var(--accent);
      outline-offset: 4px;
    }

    &::before {
      position: absolute;
      inset: 0;
      display: grid;
      place-items: center;
      color: var(--muted);
      content: '图片加载失败';
      background: var(--surface-soft);
    }

    &::after {
      position: absolute;
      inset: 0;
      z-index: 1;
      pointer-events: none;
      content: '';
      background: linear-gradient(180deg, transparent 46%, rgb(35 18 18 / 82%));
    }

    img {
      position: relative;
      z-index: 1;
      aspect-ratio: 16 / 10;
      object-fit: cover;
      filter: saturate(0.84);
      transition:
        transform 520ms var(--ease),
        filter 220ms var(--ease);

      &.is-error {
        visibility: hidden;
      }
    }

    &:hover img {
      filter: saturate(1);
      transform: scale(1.025);
    }

    .profile-play {
      position: absolute;
      top: 50%;
      left: 50%;
      z-index: 2;
      display: grid;
      place-items: center;
      width: 58px;
      height: 58px;
      color: var(--white);
      background: var(--accent);
      border-radius: 50%;
      box-shadow: 0 12px 28px rgb(58 26 27 / 28%);
      transform: translate(-50%, -50%);
      transition:
        opacity 180ms var(--ease),
        transform 180ms var(--ease);

      i {
        width: 22px;
        height: 22px;
        margin-left: 3px;
      }
    }

    .profile-media-state {
      position: absolute;
      right: 18px;
      bottom: 16px;
      left: 18px;
      z-index: 2;
      font-size: 12px;
      color: var(--white);
      text-align: left;
    }
  }

  // ── 企业文化 culture-section ──
  .culture-gallery {
    --eg-active: 1.6;
    --eg-inactive: 0.75;

    margin-top: 42px;
  }

  .culture-card {
    position: relative;
    display: grid;
    flex: 1;
    grid-template-rows: auto auto;
    grid-template-columns: 58px 1fr;
    gap: 8px 18px;
    align-content: center;
    align-items: center;
    height: 220px;
    padding: 26px;
    overflow: hidden;
    color: var(--fg);
    text-align: left;
    cursor: pointer;
    background: var(--surface);
    border: 1px solid #e8d4d5;
    border-radius: var(--radius-md);
    transition:
      flex 300ms var(--ease),
      color 220ms var(--ease),
      background 220ms var(--ease),
      box-shadow 220ms var(--ease);

    &::before {
      position: absolute;
      inset: 0;
      content: '';
      background-image:
        linear-gradient(90deg, rgb(104 23 27 / 94%), rgb(181 40 45 / 76%)),
        url('/images/company/culture-gallery.webp');
      background-position: center;
      background-size: cover;
      opacity: 0;
      transform: scale(1.04);
      transition:
        opacity 300ms var(--ease),
        transform 700ms var(--ease);
    }

    > * {
      position: relative;
      z-index: 1;
    }

    &.active {
      gap: 8px 22px;
      padding-inline: 42px;
      color: var(--white);
      text-align: left;
      background: #68171b;
      border-color: var(--accent);
      box-shadow: 0 18px 38px rgb(200 32 42 / 22%);

      &::before {
        opacity: 1;
        transform: scale(1);
      }

      .culture-icon {
        width: 72px;
        height: 72px;
        color: var(--white);
        border: 1px solid rgb(255 255 255 / 44%);
        border-radius: 50%;
      }

      .culture-detail {
        visibility: visible;
        width: auto;
        opacity: 1;
      }
    }
  }

  .culture-icon {
    display: grid;
    grid-row: 1 / 3;
    place-items: center;
    justify-self: center;
    width: 58px;
    height: 58px;
    color: var(--accent);
    transition: color 260ms var(--ease);

    svg {
      width: 44px;
      height: 44px;
      overflow: visible;
    }
  }

  .culture-label {
    grid-column: 2;
    align-self: end;
    font-size: 17px;
    font-weight: 620;
    white-space: nowrap;
  }

  .culture-detail {
    visibility: hidden;
    grid-column: 2;
    width: 0;
    max-width: 24em;
    height: 3.3em;
    overflow: hidden;
    font-size: 12px;
    line-height: 1.65;
    color: rgb(255 255 255 / 78%);
    opacity: 0;
    transition: opacity 180ms var(--ease);
  }

  .swipe-hint {
    display: none;
    margin: 18px 0 0;
    font-size: 12px;
    color: var(--muted);
    text-align: center;
  }

  // ── 发展历程 milestones-section ──
  .milestones-section {
    color: var(--white);
    background:
      linear-gradient(110deg, rgb(78 8 14 / 96%), rgb(169 22 32 / 88%)),
      url('/images/company/campus.webp') center / cover;

    h2,
    .section-kicker {
      color: var(--white);
    }
  }

  .milestones-intro {
    max-width: 44em;
    margin: 16px 0 0;
    color: rgb(255 255 255 / 76%);
  }

  .milestone-track {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    margin-top: 34px;
    border-block: 1px solid rgb(255 255 255 / 25%);
  }

  .milestone-card {
    position: relative;
    min-height: 230px;
    padding: 24px;
    color: var(--white);
    text-align: left;
    cursor: pointer;
    background: transparent;
    border: 0;
    border-right: 1px solid rgb(255 255 255 / 18%);
    transition: background 180ms var(--ease);

    &:last-child {
      border-right: 0;
    }

    &::after {
      position: absolute;
      right: 24px;
      bottom: 0;
      left: 24px;
      height: 3px;
      content: '';
      background: var(--white);
      opacity: 0;
      transform: scaleX(0.5);
      transition:
        opacity 180ms var(--ease),
        transform 180ms var(--ease);
    }

    &:hover,
    &:focus-visible,
    &.active {
      background: rgb(255 255 255 / 12%);
    }

    &:focus-visible {
      z-index: 1;
      outline: 2px solid var(--white);
      outline-offset: -4px;
    }

    &.active::after {
      opacity: 1;
      transform: scaleX(1);
    }

    .milestone-year {
      display: block;
      min-height: 30px;
      font: 700 20px/1.5 var(--num);
      color: rgb(255 255 255 / 82%);
      word-break: keep-all;
    }

    .milestone-title {
      display: block;
      margin: 24px 0 10px;
      font-size: 20px;
      line-height: 1.45;
    }

    .milestone-summary {
      display: none;
      font-size: 13px;
      font-style: normal;
      line-height: 1.72;
      color: rgb(255 255 255 / 72%);
    }

    &.active {
      .milestone-year {
        color: var(--white);
      }

      .milestone-summary {
        display: block;
        color: rgb(255 255 255 / 90%);
        animation: milestone-summary-in 220ms var(--ease) both;
      }
    }
  }

  .milestone-controls {
    display: flex;
    gap: 18px;
    align-items: center;
    justify-content: center;
    margin-top: 24px;

    button {
      display: grid;
      place-items: center;
      width: 44px;
      height: 44px;
      padding: 0;
      color: var(--white);
      cursor: pointer;
      background: transparent;
      border: 1px solid rgb(255 255 255 / 35%);
      border-radius: 50%;
      transition:
        border-color 180ms var(--ease),
        background 180ms var(--ease),
        opacity 180ms var(--ease);

      &:hover:not(:disabled),
      &:focus-visible {
        background: rgb(255 255 255 / 14%);
        border-color: rgb(255 255 255 / 72%);
      }

      &:focus-visible {
        outline: 2px solid var(--white);
        outline-offset: 3px;
      }

      &:disabled {
        cursor: default;
        opacity: 0.35;
      }
    }
  }

  .milestone-current {
    min-width: 120px;
    font-size: 12px;
    color: rgb(255 255 255 / 74%);
    text-align: center;
  }

  // ── 荣誉资质 honors ──
  .honors-layout {
    grid-template-columns: 1.06fr 0.94fr;
  }

  .honor-stage,
  .team-visual {
    position: relative;
    margin: 0;
    overflow: hidden;
    background: var(--surface-soft);
    border-radius: var(--radius-md);
    box-shadow: 0 22px 58px rgb(58 26 27 / 14%);

    img {
      position: relative;
      z-index: 1;
      aspect-ratio: 16 / 10;
      object-fit: cover;

      &.is-error {
        visibility: hidden;
      }
    }

    &::before {
      position: absolute;
      inset: 0;
      display: grid;
      place-items: center;
      color: var(--muted);
      content: '图片加载失败';
      background: var(--surface-soft);
    }

    figcaption {
      position: absolute;
      right: 14px;
      bottom: 12px;
      left: 14px;
      z-index: 2;
      font-size: 11px;
      color: rgb(255 255 255 / 82%);
    }

    &::after {
      position: absolute;
      inset: 45% 0 0;
      z-index: 1;
      pointer-events: none;
      content: '';
      background: linear-gradient(transparent, rgb(35 18 18 / 82%));
    }
  }

  // ── 研发团队 team ──
  .team-layout {
    grid-template-columns: minmax(320px, 0.86fr) minmax(520px, 1.14fr);
    gap: 52px clamp(64px, 8vw, 112px);
    align-items: center;
  }

  .team-copy {
    grid-column: 1;
    text-align: left;

    .team-lead {
      margin: 18px 0 0;
      font-size: 18px;
      font-weight: 620;
      color: var(--accent);
    }

    > p:not(.team-lead) {
      margin: 16px 0 0;
      line-height: 1.9;
      color: var(--muted);
    }

    blockquote {
      padding: 18px 20px;
      margin: 24px 0 0;
      color: var(--fg);
      background: var(--red-soft);
      border-left: 3px solid var(--accent);
    }
  }

  .team-showcase {
    grid-column: 2;
  }

  .team-grid {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    grid-column: 1 / -1;
    gap: 1px;
    overflow: hidden;
    background: var(--border);
    border: 1px solid var(--border);

    article {
      display: flex;
      gap: 13px;
      align-items: center;
      min-height: 92px;
      padding: 16px 18px;
      background: var(--surface);
    }

    h3 {
      margin: 0;
      font-size: 15px;
      white-space: nowrap;
    }
  }

  .team-icon {
    display: grid;
    flex: 0 0 34px;
    place-items: center;
    width: 34px;
    height: 34px;
    color: var(--accent);

    svg {
      width: 28px;
      height: 28px;
      overflow: visible;
    }
  }

  // ── 员工风采 staff ──
  .staff-mosaic {
    display: grid;
    grid-template-rows: 240px 240px;
    grid-template-columns: 1.2fr 0.8fr 0.8fr;
    gap: 4px;
    margin-top: 36px;
  }

  .staff-item {
    position: relative;
    min-height: 0;
    margin: 0;
    overflow: hidden;
    background: var(--fg);
    border-radius: 5px;

    &:first-child {
      grid-row: 1 / 3;
    }

    &::before {
      position: absolute;
      inset: 0;
      display: grid;
      place-items: center;
      color: var(--muted);
      content: '图片加载失败';
      background: var(--surface-soft);
    }

    img {
      position: relative;
      z-index: 1;
      height: 100%;
      object-fit: cover;
      transition:
        transform 420ms var(--ease),
        opacity 220ms var(--ease);

      &.is-error {
        visibility: hidden;
      }
    }

    &:hover {
      .staff-overlay {
        opacity: 1;
        transform: none;
      }

      img {
        opacity: 0.72;
        transform: scale(1.035);
      }
    }
  }

  .staff-overlay {
    position: absolute;
    inset: auto 0 0;
    z-index: 2;
    padding: 22px;
    color: var(--white);
    background: linear-gradient(transparent, rgb(35 22 22 / 90%));
    opacity: 0;
    transform: translateY(12px);
    transition:
      opacity 220ms var(--ease),
      transform 220ms var(--ease);

    strong,
    span {
      display: block;
    }

    span {
      margin-top: 7px;
      font-size: 12px;
      line-height: 1.55;
      color: rgb(255 255 255 / 72%);
    }
  }
</style>
